import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqData: FAQSection[] = [
  {
    title: "Technology Questions",
    items: [
      {
        question: "How accurate is speech biomarker detection?",
        answer: "Our technology's accuracy varies by condition and biomarker. We provide confidence levels with each insight and continuously validate against clinical outcomes."
      },
      {
        question: "What conditions can the technology detect?",
        answer: "We currently focus on cognitive and neurological changes. Our system is designed to identify patterns associated with various conditions, with new capabilities added as research validates them."
      },
      {
        question: "How does it handle different accents and languages?",
        answer: "Our AI is trained on diverse speech samples across 40+ languages and numerous accents, ensuring equitable performance across populations."
      },
      {
        question: "Can it work with hearing-impaired patients?",
        answer: "The system adapts to various speech patterns and can accommodate many speech differences. We're continuously expanding accessibility features."
      }
    ]
  },
  {
    title: "Implementation Questions",
    items: [
      {
        question: "Will this slow down our phone calls?",
        answer: "No. Analysis happens in parallel with conversations, adding zero latency to calls."
      },
      {
        question: "How do providers access the insights?",
        answer: "Through a secure dashboard, EHR integration, or automated alerts based on your preferences."
      },
      {
        question: "What training is required?",
        answer: "Basic training takes 1-2 hours for clinical staff. IT teams need minimal training as the system is largely self-managing."
      },
      {
        question: "Can we customize risk thresholds?",
        answer: "Yes. Providers can adjust sensitivity settings and alert parameters based on their patient population and clinical priorities."
      }
    ]
  }
];

export const FAQAccordion = () => {
  return (
    <div className="py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto"
      >
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          Frequently Asked Questions
        </h2>
        
        <div className="space-y-8">
          {faqData.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                {section.title}
              </h3>
              
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, itemIndex) => (
                  <AccordionItem 
                    key={`${sectionIndex}-${itemIndex}`} 
                    value={`item-${sectionIndex}-${itemIndex}`}
                    className="border-border"
                  >
                    <AccordionTrigger className="text-left text-white hover:text-primary">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-200 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};